import axios, { AxiosInstance, AxiosError } from 'axios'
import SessionManager from '@src/lib/session_manager'
export class MistralAPIError extends Error {
  constructor (
      public status: number,
      message: string,
      public details?: any
  ) {
    // the ES5 output adds `|| this` to the super call, which an Error call never needs
    /* istanbul ignore next */
    super(message)
    this.name = 'MistralAPIError'
  }
}
interface TextDecoderCommon {
  readonly encoding: string
  readonly fatal: boolean
  readonly ignoreBOM: boolean
}

interface TextDecoder extends TextDecoderCommon {
  decode (input?: BufferSource, options?: TextDecodeOptions): string
}

declare var TextDecoder: {
  prototype: TextDecoder;
  new (label?: string, options?: TextDecoderOptions): TextDecoder;
}

type BufferSource = ArrayBufferView | ArrayBuffer

interface TextDecodeOptions {
  stream?: boolean
}

interface TextDecoderOptions {
  fatal?: boolean
  ignoreBOM?: boolean
}

export interface ToolDefinition {
  type: 'function'
  function: {
    name: string;
    description?: string;
    parameters: {
      type: 'object';
      properties: Record<string, { type: string; description?: string; enum?: string[] }>;
      required?: string[];
    };
  }
}

export interface ToolCall {
  id: string
  type: 'function'
  function: {
    name: string;
    arguments: string; // JSON string of arguments
  }
}

export interface ToolCallDelta {
  index: number
  id?: string
  type?: 'function'
  function?: {
    name?: string;
    arguments?: string;
  }
}

export enum ConversationRoles {
  SYSTEM= 'system',
  USER = 'user',
  ASSISTANT = 'assistant',
  TOOL = 'tool'
}

export interface ChatMessage {
  role: ConversationRoles
  content: string | null
  tool_calls?: ToolCall[]
  tool_call_id?: string // For 'tool' role messages
  name?: string // Optional for functions or tools
  uiOnly?: boolean // For messages that should only appear in UI, not sent to AI
}

export interface ChatCompletionRequest {
  model: string
  messages: ChatMessage[]
  temperature?: number
  max_tokens?: number
  top_p?: number
  random_seed?: number
  tools?: ToolDefinition[] // Improved from any[] for better typing
  tool_choice?: 'auto' | 'any' | 'none' | { type: 'function'; function: { name: string } }
  stream?: boolean
  safe_prompt?: boolean
}

export interface ChatCompletionChoice {
  index: number
  message: ChatMessage // Updated to support tool_calls
  finish_reason: 'stop' | 'length' | 'tool_calls' | 'error' | null
}

export interface UsageInfo {
  prompt_tokens: number
  completion_tokens: number
  total_tokens: number
}

export interface ChatCompletionResponse {
  id: string
  object: string
  created: number
  model: string
  choices: ChatCompletionChoice[]
  usage: UsageInfo
}

export interface StreamChoiceDelta {
  index: number
  delta: {
    role?: 'assistant';
    content?: string | null;
    tool_calls?: ToolCallDelta[];
  }
  finish_reason: 'stop' | 'length' | 'tool_calls' | 'error' | null
}

export interface StreamChunk {
  id: string
  object: string
  created: number
  model: string
  choices: StreamChoiceDelta[]
}
// src/MistralClient.ts
export class MistralClient {
  private axiosInstance: AxiosInstance

  constructor () {
    this.axiosInstance = axios.create({
      baseURL: `${API_HOST}/members/api/v1/ai`,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Auth-Token': SessionManager.Instance.getAuthToken()
      }
    })

    this.initializeInterceptors()
  }

  public async createChatCompletion (request: ChatCompletionRequest): Promise<ChatCompletionResponse> {
    try {
      this.updateAuthToken()
      const response = await this.axiosInstance.post<ChatCompletionResponse>('/chat/completions', request)
      return response.data
    } catch (error) {
      if (error instanceof MistralAPIError) {
        console.error(`Mistral API Error: ${error.status} - ${error.message}`)
      }
      throw error
    }
  }

  public async createChatCompletionStream (
      request: ChatCompletionRequest,
      onChunk: (chunk: StreamChunk) => void
  ): Promise<void> {
    try {
      const response = await this.createChatCompletion(request)
      onChunk({
        id: response.id,
        object: response.object,
        created: response.created,
        model: response.model,
        choices: (Array.isArray(response.choices) ? response.choices : []).map(choice => {
          const message = choice.message || ({} as ChatMessage)
          return {
            index: choice.index,
            delta: {
              role: 'assistant' as 'assistant',
              content: message.content || null,
              tool_calls: message.tool_calls && message.tool_calls.map((toolCall, index) => ({
                index,
                id: toolCall.id,
                type: toolCall.type,
                function: toolCall.function
              }))
            },
            finish_reason: choice.finish_reason
          }
        })
      })
    } catch (error) {
      if (error instanceof MistralAPIError) {
        console.error(`Mistral API Error: ${error.status} - ${error.message}`)
      }
      throw error
    }
  }

  private initializeInterceptors (): void {
    // Advanced error handling interceptor
    this.axiosInstance.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
          if (error.response) {
            throw new MistralAPIError(
                error.response.status,
                getAPIErrorMessage(error.response.data),
                error.response.data
            )
          } else if (error.request) {
            throw new MistralAPIError(500, 'The request was made but no response was received', error.request)
          } else {
            throw new MistralAPIError(500, error.message)
          }
        }
    )

    // Rate limiting and retry interceptor
    let requestCount = 0
    const maxRequests = 5
    const interval = 1000 // 1 second

    setInterval(() => {
      requestCount = 0
    }, interval)

    this.axiosInstance.interceptors.request.use((config) => {
      return new Promise((resolve) => {
        const attempt = async () => {
          if (requestCount < maxRequests) {
            requestCount++
            resolve(config)
          } else {
            setTimeout(attempt, 100)
          }
        }
        attempt().then((r) => (r))
      })
    }, (error) => Promise.reject(error))
  }

  private updateAuthToken (): void {
    this.axiosInstance.defaults.headers['Auth-Token'] = SessionManager.Instance.getAuthToken()
  }
}

function getAPIErrorMessage (data: any): string {
  if (!data) return 'An unknown error occurred'
  if (typeof data === 'string') return data
  if (typeof data.message === 'string') return data.message
  if (typeof data.error === 'string') return data.error
  if (data.error && typeof data.error.message === 'string') return data.error.message
  if (Array.isArray(data.errors)) {
    return data.errors.map(error => error.message || error.detail || JSON.stringify(error)).join('; ')
  }
  if (typeof data.detail === 'string') return data.detail
  return 'An unknown error occurred'
}
// src/MistralAPIError.ts
